import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import PasswordInput from './PasswordInput';

const F = { fontFamily: 'Inter' } as const;

/**
 * Standart "Profili Düzenle" görünümü — öğrenci ve öğretmen profillerinde
 * ortak (T16 devamı: iki rol arasındaki düzen tutarsızlığını kapatır).
 * İki eşit genişlikte kart: "Bilgilerim" (Ad/Soyad/E-posta/Telefon düzenlenebilir
 * + role özel salt-okunur alanlar) ve "Şifre Değiştir" (kompakt üçlü form).
 * Kaydet sonrası AuthContext.refresh() ile /auth/me yeniden çekilir, ayrıca
 * localStorage'daki cached user senkronlanır.
 */
const ProfileEditView: React.FC<{
  onBack: () => void;
  /** Role özel değiştirilemez alanlar (öğrenci: Okul/Sınıf/Kitap Kodu; öğretmen: Okul/Branş/Seviye). */
  readOnlyRows: { label: string; value: string }[];
  /** Form ön-dolumu için kaynak user (sayfa `fresh ?? user` geçirebilir). Verilmezse context user. */
  source?: any;
  /** Başarılı bilgi kaydından sonra PATCH yanıtıyla çağrılır (ör. sayfanın `fresh` state'ini güncellemek için). */
  onSaved?: (freshUser: any) => void;
  /** AP62: veli profili için Meslek alanını da düzenlenebilir yap (backend updateMe occupation'ı destekliyor). */
  showOccupation?: boolean;
}> = ({ onBack, readOnlyRows, source, onSaved, showOccupation }) => {
  const { user, refresh } = useAuth();
  const src: any = source ?? user;

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', occupation: '' });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoMsg, setInfoMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    const name = src?.name ? String(src.name) : '';
    const surname = src?.surname ?? (name.split(' ').slice(-1)[0] ?? '');
    const firstName = surname && name.endsWith(surname)
      ? name.slice(0, name.length - surname.length).trim() || name
      : name;
    setForm({
      firstName,
      lastName: surname,
      email: src?.email ?? '',
      phone: src?.phone ?? '',
      occupation: src?.parent_occupation ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveInfo = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setInfoMsg({ kind: 'err', text: 'Ad, soyad ve e-posta zorunlu.' });
      return;
    }
    setInfoSaving(true);
    setInfoMsg(null);
    try {
      const r = await authApi.updateMe({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ...(showOccupation ? { occupation: form.occupation.trim() || null } : {}),
      });
      // localStorage'daki cached user'ı da güncelle ki sayfa yenilense bile yeni veriler görünsün
      try {
        const cached = JSON.parse(localStorage.getItem('dijipo_user') ?? 'null');
        if (cached) localStorage.setItem('dijipo_user', JSON.stringify({ ...cached, ...r.data }));
      } catch { /* ignore */ }
      await refresh().catch(() => { /* context refresh best-effort */ });
      onSaved?.(r.data);
      setInfoMsg({ kind: 'ok', text: 'Bilgiler güncellendi.' });
    } catch (e: any) {
      setInfoMsg({ kind: 'err', text: e?.response?.data?.error ?? 'Güncellenemedi' });
    } finally {
      setInfoSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMsg(null);
    if (!oldPassword || !newPassword) { setPasswordMsg({ kind: 'err', text: 'Tüm alanları doldur' }); return; }
    if (newPassword !== confirmPassword) { setPasswordMsg({ kind: 'err', text: 'Yeni şifreler eşleşmiyor' }); return; }
    if (newPassword.length < 8) { setPasswordMsg({ kind: 'err', text: 'Yeni şifre en az 8 karakter olmalı' }); return; }
    setPasswordSaving(true);
    try {
      await authApi.changePassword(oldPassword, newPassword);
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      setPasswordMsg({ kind: 'ok', text: 'Şifre değiştirildi.' });
    } catch (e: any) {
      setPasswordMsg({ kind: 'err', text: e?.response?.data?.error ?? 'Şifre değiştirilemedi' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const labelStyle = { ...F, fontSize: 13, fontWeight: 600, color: '#3973bf', display: 'block', marginBottom: 4 } as const;
  const inputClass = 'w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[14px] text-[#2b2b2b]';
  const inputStyle = { border: '1.5px solid #d4d4d4', ...F } as const;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack}
          className="w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round"><path d="M13 4l-6 6 6 6" /></svg>
        </button>
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Profili Düzenle</h1>
      </div>

      <div className="max-w-[1100px] space-y-5">
        {/* Bilgilerim */}
        <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <h2 className="mb-5" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Bilgilerim</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-[760px]">
            <label className="block">
              <span style={labelStyle}>Ad</span>
              <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className={inputClass} style={inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>Soyad</span>
              <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className={inputClass} style={inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>E-posta</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputClass} style={inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>Telefon</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputClass} style={inputStyle} />
            </label>
            {showOccupation && (
              <label className="block">
                <span style={labelStyle}>Meslek</span>
                <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                  className={inputClass} style={inputStyle} placeholder="—" />
              </label>
            )}
          </div>

          {readOnlyRows.length > 0 && (
            <div className="mt-5 pt-5 border-t border-[#eeeeee] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4 max-w-[760px]">
              {readOnlyRows.map((r, i) => (
                <div key={i} className="min-w-0">
                  <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888', marginBottom: 4 }}>{r.label}</p>
                  <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="break-words">{r.value}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end gap-4 mt-6">
            {infoMsg && (
              <span className={`text-[13px] ${infoMsg.kind === 'ok' ? 'text-green-600' : 'text-red-500'}`} style={F}>
                {infoMsg.text}
              </span>
            )}
            <button onClick={handleSaveInfo} disabled={infoSaving}
              className="rounded-[12px] px-6 py-2.5 hover:opacity-90 disabled:opacity-50"
              style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fafafa' }}>
                {infoSaving ? 'Kaydediliyor…' : 'Bilgileri Kaydet'}
              </span>
            </button>
          </div>
        </div>

        {/* Şifre Değiştir */}
        <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <h2 className="mb-5" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Şifre Değiştir</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 max-w-[760px]">
            <label className="block">
              <span style={labelStyle}>Eski Şifre</span>
              <PasswordInput compact value={oldPassword} onChange={setOldPassword} />
            </label>
            <label className="block">
              <span style={labelStyle}>Yeni Şifre</span>
              <PasswordInput compact value={newPassword} onChange={setNewPassword} />
            </label>
            <label className="block">
              <span style={labelStyle}>Yeni Şifre (Tekrar)</span>
              <PasswordInput compact value={confirmPassword} onChange={setConfirmPassword} />
            </label>
          </div>

          <p className="mt-4" style={{ ...F, fontSize: 13, color: '#888', lineHeight: 1.5 }}>
            Şifre en az 8 karakter olmalı; en az 1 büyük harf, 1 küçük harf, 1 rakam ve özel karakter içermelidir.
          </p>

          <div className="flex items-center justify-end gap-4 mt-4">
            {passwordMsg && (
              <span className={`text-[13px] ${passwordMsg.kind === 'ok' ? 'text-green-600' : 'text-red-500'}`} style={F}>
                {passwordMsg.text}
              </span>
            )}
            <button onClick={handleChangePassword} disabled={passwordSaving}
              className="rounded-[12px] px-6 py-2.5 hover:opacity-90 disabled:opacity-50"
              style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fafafa' }}>
                {passwordSaving ? 'Kaydediliyor…' : 'Şifreyi Değiştir'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditView;
